import { apiTransport } from './api-client';
import { documentStatus } from './document-status';
import type { Page } from './collections';
import type { AuditEntry } from '@micarahr/shared-types';
import type { WebNotification } from './operations';
import { narrow, AUDIT_MODULES, AUDIT_OUTCOMES, AUDIT_SEVERITIES, USER_ROLES } from './narrow';
import { auditActionLabel, auditRecordText } from '../lib/audit-labels';
import { humanizeNotificationText, notificationTitle } from '../lib/notification-text';

import type {
  ApiDepartment,
  ApiNamedRecord,
  ApiPosition,
  ApiTeam,
  ApiWorkLocation,
} from './organization.api';

/**
 * Registries: departments, teams, positions, job levels, cost centres, work
 * locations — and the read-only surfaces, audit and notifications.
 *
 * These are the modules where the prototype's shape and the API's are closest,
 * so the adapter is thin by design. The one thing it does add is refusing to
 * flatten a registry into an unpaginated array: a directory of departments is
 * small today and a list of audit events is not, and the same helper serving
 * both means neither grows a bespoke fetch when it gets large.
 */

interface RegistryFilter {
  search?: string;
  includeInactive?: boolean;
  page?: number;
  pageSize?: number;
}

/**
 * A registry, read through the shared factory the API exposes for all of them.
 *
 * `T` is the frontend's row type. No mapping happens here because these
 * endpoints already return flat records — the moment one grows a nested
 * relation the screens read flat, it needs an explicit mapper like the
 * directory has, not a wider cast.
 */
function registry<T>(path: string) {
  return {
    async list(filter: RegistryFilter = {}): Promise<Page<T>> {
      const pageSize = filter.pageSize ?? 100;
      const page = await apiTransport().page<T>(`/api/v1/${path}`, {
        query: {
          search: filter.search,
          includeInactive: filter.includeInactive || undefined,
          limit: pageSize,
        },
      });

      return {
        rows: page.items,
        total: page.totalCount,
        page: filter.page ?? 1,
        pageSize,
      };
    },

    /** Every row, for a picker. Bounded, because a picker is not a report. */
    async all(): Promise<T[]> {
      const page = await apiTransport().page<T>(`/api/v1/${path}`, { query: { limit: 200 } });
      return page.items;
    },

    async get(id: string): Promise<T | null> {
      try {
        return await apiTransport().get<T>(`/api/v1/${path}/${id}`);
      } catch (error) {
        if ((error as { status?: number }).status === 404) return null;
        throw error;
      }
    },

    create(input: Record<string, unknown>): Promise<T> {
      return apiTransport().post<T>(`/api/v1/${path}`, input);
    },

    update(id: string, input: Record<string, unknown>): Promise<T> {
      return apiTransport().patch<T>(`/api/v1/${path}/${id}`, input);
    },

    /**
     * Deactivate rather than delete.
     *
     * Employment history points at these records — a department somebody worked
     * in five years ago has to keep existing for their history to make sense.
     * The API has no delete for the same reason.
     *
     * Through the dedicated route, not a PATCH. The update schemas do not
     * accept `isActive` — Zod strips it, so `PATCH {isActive: false}` was a
     * silent no-op that reported success — and the deactivate route is where
     * the server's audit entry and safety checks live.
     */
    deactivate(id: string): Promise<T> {
      return apiTransport().post<T>(`/api/v1/${path}/${id}/deactivate`, {});
    },

    reactivate(id: string): Promise<T> {
      return apiTransport().post<T>(`/api/v1/${path}/${id}/reactivate`, {});
    },
  };
}

/*
 * Each registry knows its row shape.
 *
 * These were all `registry<Record<string, unknown>>`, which made every caller
 * cast the result before it could read a field — and a cast that turns an
 * untyped bag into a `Department` also silences a missing `code` or a renamed
 * `headEmployeeId`. The shapes are declared beside the mappers that consume
 * them, and imported here as types only, so nothing circular exists at runtime.
 */
export const apiDepartmentService = registry<ApiDepartment>('departments');
export const apiTeamService = registry<ApiTeam>('teams');
export const apiPositionService = registry<ApiPosition>('positions');
export const apiJobLevelService = registry<ApiNamedRecord>('job-levels');
export const apiCostCentreService = registry<ApiNamedRecord>('cost-centers');
export const apiWorkLocationService = registry<ApiWorkLocation>('work-locations');
export const apiPayrollGroupService = registry<ApiNamedRecord>('payroll-groups');

/**
 * Audit events.
 *
 * Read-only by construction: the API has no update or delete path, which is
 * what makes the immutability claim real rather than a UI without a delete
 * button.
 * The adapter therefore exposes no mutation either, so a future screen cannot
 * accidentally offer one.
 */
interface ApiAuditEvent {
  id: string;
  occurredAt: string;
  actorUserId: string | null;
  actorName: string | null;
  actorRoles: string[];
  module: string;
  action: string;
  summary: string | null;
  /** The action as a phrase, the words the server's search matches. */
  actionLabel?: string;
  /** The record as "Type · identifier", likewise. */
  recordText?: string;
  recordIdentifier?: string | null;
  /** What of the entry this reader is not shown. */
  withheldDetail?: string[];
  recordType: string | null;
  recordId: string | null;
  recordLabel: string | null;
  subjectEmployeeId: string | null;
  subjectName: string | null;
  outcome: string;
  severity: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  /** The names behind the ids in before/after, for a reader who may see them. */
  references?: Record<string, string>;
  metadata: Record<string, unknown> | null;
  requestId: string | null;
  ipAddress: string | null;
}

/**
 * Map an audit event onto the log's row.
 *
 * `before`, `after` and `metadata` pass through as the server sent them,
 * redaction included. The server already removed what the caller may not see;
 * re-deriving a summary here would either duplicate that judgement or
 * contradict it.
 */
function toAuditEntry(row: ApiAuditEvent): AuditEntry {
  const actionLabel = row.actionLabel ?? auditActionLabel(row.action, row.recordType);
  return {
    id: row.id,
    occurredAt: row.occurredAt,
    actorId: row.actorUserId ?? '',
    actorName: row.actorName ?? 'System',
    actorRoles: row.actorRoles.map((role) => narrow(USER_ROLES, role, 'role')),
    module: narrow(AUDIT_MODULES, row.module, 'audit module'),
    action: row.action,
    // The sentence the writer recorded, else the action as a phrase. Never
    // the dotted code: that is what the log used to show in its Action column.
    // The phrase and the record's words are the server's, which its search
    // matches: what a row shows is what finds it.
    summary: row.summary ?? actionLabel,
    actionLabel,
    recordText:
      row.recordText ??
      auditRecordText({
        action: row.action,
        recordType: row.recordType,
        recordLabel: row.recordLabel,
        recordIdentifier: row.recordIdentifier ?? null,
        subjectName: row.subjectName,
      }),
    withheldDetail: row.withheldDetail ?? [],
    recordType: row.recordType ?? '',
    recordId: row.recordId ?? '',
    recordLabel: row.recordLabel ?? '',
    subjectEmployeeId: row.subjectEmployeeId,
    subjectName: row.subjectName ?? null,
    outcome: narrow(AUDIT_OUTCOMES, row.outcome, 'audit outcome'),
    severity: narrow(AUDIT_SEVERITIES, row.severity, 'audit severity'),
    before: row.before,
    after: row.after,
    references: row.references ?? {},
    metadata: row.metadata ?? {},
    requestId: row.requestId,
    ipAddress: row.ipAddress,
    // The server records the request's user agent against the event; this
    // projection does not return it. Null, not this browser's — reading
    // `navigator.userAgent` here would label somebody else's action with the
    // agent of whoever happens to be viewing the log.
    userAgent: null,
  };
}

interface ApiEmployeeDocument {
  id: string;
  status: string;
  expiresOn: string | null;
  verifiedAt: string | null;
}

/**
 * The documents rail, counted from the same list the table renders.
 *
 * The summary was still the prototype\'s, so the rail announced 619 documents
 * above a table honestly reporting none — the split-brain failure again, and
 * on this screen the two halves sat six centimetres apart.
 */
export const apiDocumentSummaryService = {
  async summary() {
    const rows = await apiTransport().get<ApiEmployeeDocument[]>('/api/v1/documents');
    // The badge's own rule, so a tile and the rows it counts cannot disagree.
    const statuses = rows.map((row) => documentStatus(row));
    const count = (status: string) => statuses.filter((entry) => entry === status).length;

    return {
      total: rows.length,
      valid: count('valid'),
      expiring: count('expiring'),
      expired: count('expired'),
      unverified: count('pending_verification'),
    };
  },
};

/*
 * Cursors remembered per filter, as the attendance list does: the API pages
 * by cursor and the table by page number, so page N is reached with the
 * cursor page N-1 returned.
 */
const auditCursors = new Map<string, string[]>();

export const apiAuditService = {
  /** The modules the organisation's log actually contains. */
  async modules(): Promise<string[]> {
    return apiTransport().get<string[]>('/api/v1/audit-logs/modules');
  },

  async list(
    filter: {
      module?: string;
      action?: string;
      actorUserId?: string;
      recordId?: string;
      severity?: string;
      from?: string;
      to?: string;
      search?: string;
      page?: number;
      pageSize?: number;
    } = {},
  ): Promise<Page<AuditEntry>> {
    const pageSize = filter.pageSize ?? 50;

    /*
     * 'all' is the screen's word for "no filter", and the prototype treated
     * it that way. Passed through, the server dutifully searched for entries
     * whose module is the string "all" and found none — so the audit log,
     * the one screen whose whole job is to show that things were recorded,
     * showed an organisation with no history.
     */
    const noAll = (value: string | undefined) => (value === 'all' ? undefined : value);
    const query = {
      module: noAll(filter.module),
      action: filter.action,
      actorUserId: filter.actorUserId,
      recordId: filter.recordId,
      severity: noAll(filter.severity),
      from: filter.from,
      to: filter.to,
      search: filter.search?.trim() || undefined,
      limit: pageSize,
    };

    const pageNumber = filter.page ?? 1;
    const key = JSON.stringify(query);
    const known = auditCursors.get(key) ?? [];
    const cursor = pageNumber <= 1 ? undefined : known[Math.min(pageNumber - 2, known.length - 1)];

    const page = await apiTransport().page<ApiAuditEvent>('/api/v1/audit-logs', {
      query: { ...query, cursor },
    });

    if (page.pageInfo.nextCursor) {
      const next = [...known];
      next[pageNumber - 1] = page.pageInfo.nextCursor;
      auditCursors.set(key, next);
    }

    return {
      rows: page.items.map(toAuditEntry),
      total: page.totalCount,
      page: pageNumber,
      pageSize,
    };
  },

  /**
   * The dashboard's "Administrative activity": the latest entries the viewer's
   * audit access lets them read, less sign-ins.
   *
   * Without this the API adapter fell through to the prototype's, which reads
   * the browser's own store — so an administrator with dozens of recorded
   * actions was told "No activity yet". Sign-ins are half the log and say
   * nothing about the state of anyone's people, so a generous page is read and
   * they are left out of it.
   */
  async recentActivity(limit = 12): Promise<AuditEntry[]> {
    const page = await apiTransport().page<ApiAuditEvent>('/api/v1/audit-logs', {
      query: { limit: 200 },
    });
    return page.items
      .map(toAuditEntry)
      .filter((entry) => entry.module !== 'auth')
      .slice(0, limit);
  },
};

/**
 * Notifications.
 *
 * The unread count is fetched separately rather than derived from the list.
 * A list is paginated and a count is not — counting the first page would
 * report "12 unread" to somebody with two hundred.
 */
interface ApiNotification {
  id: string;
  type: string;
  title: string;
  body: string;
  actionPath: string | null;
  readAt: string | null;
  createdAt: string;
  employeeId: string | null;
}

/**
 * Map a notification onto the shape the shell renders.
 *
 * `readAt` is a timestamp on the server and a boolean on the screen. Keeping
 * the timestamp would be more faithful, but the shell only ever asks whether
 * it was read — and a nullable date rendered as a badge is how "read at
 * midnight" becomes an unread marker.
 */
function toWebNotification(row: ApiNotification): WebNotification {
  return {
    id: row.id,
    // The type code stays the category; the shell labels it. Title and body
    // are worded for rows written before the server wrote plain dates.
    category: row.type,
    title: notificationTitle(row.type, row.title),
    body: humanizeNotificationText(row.body),
    href: row.actionPath ?? '',
    read: row.readAt !== null,
    createdAt: row.createdAt,
    ...(row.employeeId ? { employeeId: row.employeeId } : {}),
  };
}

export const apiNotificationService = {
  async list(filter: { unreadOnly?: boolean; limit?: number } = {}): Promise<WebNotification[]> {
    const page = await apiTransport().page<ApiNotification>('/api/v1/notifications', {
      query: { unreadOnly: filter.unreadOnly || undefined, limit: filter.limit ?? 50 },
    });
    return page.items.map(toWebNotification);
  },

  async unreadCount(): Promise<number> {
    const result = await apiTransport().get<{ unread: number }>(
      '/api/v1/notifications/unread-count',
    );
    // `unread`, not `count`. Reading the wrong key returned undefined, which
    // TanStack Query rejects outright — the badge did not merely show nothing,
    // the query threw.
    return result.unread;
  },

  markRead(id: string) {
    return apiTransport().post(`/api/v1/notifications/${id}/read`, {});
  },

  markAllRead() {
    return apiTransport().post('/api/v1/notifications/read-all', {});
  },
};
